import { Collapse, Divider, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import type {
  ChartOfAccountsItem,
  ConditionField,
  DimensionDictionary,
} from "../../../../../../../../../api/types/svTypes.ts";
import { DimensionNames } from "../../../../../utilities/DimensionNames.ts";
import MetaIcon from "../../../../common/MetaIcon.tsx";
import { formatCaption } from "../../../../viewer/common/conditions/conditionsHelper.tsx";
import useSelectedDictionaryRows from "../../../../viewer/common/conditions/useSelectedDictionaryRows.ts";
import type { ChartOfAccountsContextItem } from "../contexts/ChartOfAccountsContext.ts";
import { useChartOfAccountsContext } from "../contexts/ChartOfAccountsContext.ts";
import { getFilterUniqueId } from "../helpers/chartOfAccountsContextHelper.ts";
import { useBookGrouping } from "../useBookGrouping.ts";
import FilterFieldBookGroupItem from "./FilterFieldBookGroupItem.tsx";
import { FilterFieldItem } from "./FilterFieldItem.tsx";
import FilterFieldSubItem from "./FilterFieldSubItem.tsx";

interface Props {
  condition: ConditionField;
  chartOfAccountItems?: ChartOfAccountsItem[];
  index: number;
  parentNode: ChartOfAccountsContextItem | null;
}

const getAccountingBookCode = (row: DimensionDictionary): string | null => {
  const value = row["Accounting Book"];
  return typeof value === "string" ? value : null;
};

export const Filter = ({ condition, chartOfAccountItems, parentNode, index }: Props) => {
  const { getNode } = useChartOfAccountsContext();
  const { isMultiBook, groupByBook } = useBookGrouping();
  const [expanded, setExpanded] = useState(false);
  const { rows: selectedRows, isLoading } = useSelectedDictionaryRows(condition);
  const filterId = useMemo(
    () => getFilterUniqueId(index, condition, parentNode?.identifier),
    [parentNode, index, condition]
  );

  const isMultiBookAccount =
    isMultiBook &&
    (condition.meta.name === DimensionNames.AccountNo || condition.meta.name === DimensionNames.AccountName);

  const groupedSelectedValues = useMemo(
    () => (isMultiBookAccount ? groupByBook(selectedRows, getAccountingBookCode) : null),
    [isMultiBookAccount, selectedRows, groupByBook]
  );

  const handleFormatCaption = useCallback(
    (field: ConditionField) => {
      if (expanded) return undefined;
      return formatCaption(field);
    },
    [expanded]
  );

  return (
    <Stack sx={{ position: "relative", width: "100%" }}>
      <FilterFieldItem
        field={condition}
        formatCaption={handleFormatCaption}
        startIcon={<MetaIcon icon={condition.meta.icon} />}
        isLoading={isLoading}
        onToggle={setExpanded}
        disableExpanding={selectedRows.length === 0}
        parentNode={parentNode}
        filterId={filterId}
      />
      <Stack sx={{ position: "relative", width: "100%" }}>
        <Collapse orientation="vertical" in={expanded}>
          <Divider />
          {groupedSelectedValues
            ? Object.entries(groupedSelectedValues).map(([bookTitle, items]) => (
                <FilterFieldBookGroupItem
                  key={bookTitle}
                  bookTitle={bookTitle}
                  bookGroupId={`${filterId}-book-${bookTitle}`}
                  parentNode={getNode(filterId, parentNode)}
                  pl={4.5}
                >
                  {(bookGroupNode, childPl) =>
                    items.map((item) => (
                      <FilterFieldSubItem
                        key={String(item[condition.meta.keyFieldName])}
                        chartOfAccountItems={chartOfAccountItems}
                        condition={condition}
                        dictionary={item}
                        parentNode={bookGroupNode}
                        pl={childPl}
                      />
                    ))
                  }
                </FilterFieldBookGroupItem>
              ))
            : selectedRows.map((item) => (
                <FilterFieldSubItem
                  key={String(item[condition.meta.keyFieldName])}
                  chartOfAccountItems={chartOfAccountItems}
                  condition={condition}
                  dictionary={item}
                  parentNode={getNode(filterId, parentNode)}
                />
              ))}
        </Collapse>
      </Stack>
    </Stack>
  );
};

export default Filter;
